import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import React, { useEffect, useState } from 'react';
import useIssueFlagWindow from '../../hooks/issueReport/useIssueFlagWindow.hooks';
import useIssueReport from '../../hooks/issueReport/useIssueReport.hooks';
import Colors from '../../utils/color';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';
import FloatingBanner, { BannerActionButton } from '../common/FloatingBanner';

/**
 * HITL-1499: confirmation that a flag landed, plus the Undo window.
 *
 * A FloatingBanner rather than a react-alert alert. The banner family is already
 * the app's idiom for a screen-level notice with an action button, and it can
 * carry its own interception marker on its own root; extending the shared
 * AlertTemplate would put an action slot and a marker into the component every
 * alert in the app renders through, and MUI Alert's built-in close button sits
 * outside anything we could mark.
 *
 * Non-modal by construction — absolutely positioned, no MUI Modal, no backdrop,
 * no focus trap — so no ordering control is blocked or delayed while it is up.
 *
 * Undo emits a RETRACTION at the next revision; it does not cancel the
 * revision-0 send. Committing on press is the point: a refresh, a car exit or a
 * network blip inside those 5s would otherwise lose the flag, and blips are
 * exactly what agents flag. "Undo after the window" is a no-op by construction —
 * the window closes, this unmounts, the button is gone.
 *
 * There is no X / dismiss affordance: the two actions are Undo and letting it
 * expire, and a third would read as "cancel the report", which it is not.
 */
const IssueFlagToast = () => {
  const { voidIssue } = useIssueReport();
  const flagWindow = useIssueFlagWindow();
  const expiresAt = flagWindow?.expiresAt ?? null;

  // The undo deadline is hard and otherwise invisible, so the button counts it
  // down at 1Hz. This state only drives the re-render — the number itself is
  // derived from `expiresAt` below, so a throttled tab or a slow frame shows a
  // truthful count rather than a stretched one.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!expiresAt) return undefined;
    const timer = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, [expiresAt]);

  if (!flagWindow) return null;

  const secondsLeft = Math.max(
    0,
    Math.ceil((flagWindow.expiresAt - Date.now()) / 1000)
  );

  return (
    // Green accent because this is a confirmation, not the amber advisory or the
    // plain update notice — and it reads well on the family's daintree card.
    // data-issue-report on the root keeps Undo out of agent-interception
    // detection; the guards listen on document in the capture phase, so nothing
    // on the button could stop them.
    <FloatingBanner
      accent={Colors.green}
      icon={<CheckCircleOutlineIcon />}
      data-issue-report="true"
      data-testid="issue-flag-toast"
      actions={
        // The hook takes a BARE STRING, not { issue_id }. It already dispatches
        // closeFlagWindow() itself, so the toast leaves with no extra dispatch.
        <BannerActionButton onClick={() => voidIssue(flagWindow.issueId)}>
          {`Undo ${secondsLeft}`}
        </BannerActionButton>
      }
    >
      <span style={{ fontSize: HITL3_FONT_SIZE, lineHeight: 1.4 }}>
        Issue flagged for this order
      </span>
    </FloatingBanner>
  );
};

export default IssueFlagToast;
